import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button} from 'src/ui-components/Button/button';
import {
    BulkEditIcon,
    Ellipses,
    GridIcon,
    Lightbulb,
    ListIcon,
    MoveIcon,
    Plus,
    TrashIcon,
} from 'src/ui-components/Iconpack';
import Breadcrumbs, {generateMediaFileBreadCrumbs} from 'src/ui-components/Breadcrumbs/breadcrumbs';
import MediaNoContent from 'src/components/media-no-content/MediaNoContent';
import MediaWithContent from 'src/components/media-with-content/MediaWithContent';
import TableListView from 'src/components/table-list-view/TableListView';
import {RootState, useAppDispatch} from 'src/store';
import {useSelector} from 'react-redux';
import {deleteMediaFile, getMediaFiles, searchMediaFiles, uploadFile} from 'src/actions/media';
import {toast, ToastContainer} from 'react-toastify';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import Tracker from 'src/lib/analytics/tracker';
import tracker from 'src/lib/analytics/tracker';
import {EventNames, EventProperty, ScreenNames} from 'src/lib/analytics/events';
import {CommonSpinLoader, DeleteAnimation, FileMovementAnimation} from 'src/ui-components/loaders';
import {Dialog, DialogContent, DialogTitle} from 'src/ui-components/Modal';
import SearchBar from './SearchBar';
import {FolderGrid, FolderList} from './FolderList';
import {CrudImpl as StorageSourceCrudImpl, StorageSourceCrud} from '../../api/org/storageSource/api';
import NewFolder from './NewFolder';
import {UpgradeCard} from '../../components/upgrade/UpgradeCard';
import {FeatureFlags} from '../../utils/features';
import {refreshUserProfile} from '../../actions/userProfile';
import DeleteFile from '../../components/delete-file/deleteFile';
import FileMovement from '../../components/file-movement';
import {MoveFiles} from '../../actions/pure/media';
import {Pagination} from '../../components/pagination/Pagination';
import ImageCsv from '../../components/image_csv/ImageCsv';
import {motion} from 'framer-motion';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../ui-components/Dropdown/dropdown';

const AllowedFileTypes = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/gif',
    'image/svg',
    'image/svg+xml',
];

const PAGINATION_PAYLOAD = 25;

const spring = {
    type: 'spring',
    stiffness: 700,
    damping: 30,
};

const MediaLibrary = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {folderName} = useParams();

    let breadcrumbItems = generateMediaFileBreadCrumbs({
        folder: folderName ? {name: folderName} : undefined,
        file: {name: 'Files'},
    });

    let routeFiles: FileList | null = null;
    if (location?.state?.files) {
        routeFiles = location.state.files || {};
    }

    const fileButtonUploadRef = useRef<any>(null);

    let [mediaProps, userProfileProps, metricsProps] = [
        useSelector((state: RootState) => state.media),
        useSelector((state: RootState) => state.userProfile),
        useSelector((state: RootState) => state.metrics),
    ];
    const initialTableViewValue = localStorage.getItem('tableView') !== 'false';
    const initialSelectedIcon = initialTableViewValue ? 'list' : 'grid';

    const uploadedFiles = mediaProps.files;
    const searchedFiles = mediaProps.searchResults;
    const [files, setFiles] = useState<FileList | null>();
    const [tableView, setTableView] = useState(initialTableViewValue);
    const [selectedIcon, setSelectedIcon] = useState(initialSelectedIcon);
    const [uploadFileMeta, setUploadFileMeta] = useState<any[]>([]);
    const [draggedData, setDraggedData] = useState<FileList | null>();
    const [tableData, setTableData] = useState<any[]>([]);
    const [dragDiv, setDragDiv] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [upgradeNudge, setUpgradeNudge] = useState(false);
    const [isTabletOrLarger, setIsTabletOrLarger] = useState(window.innerWidth > 768);
    const [selectAll, setSelectAll] = useState(false);
    const [selectChecked, setSelectChecked] = useState(false);
    const [selectedCount, setSelectedCount] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState<string[]>();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [clearSelection, setClearSelection] = useState<boolean>(false);
    const [deletingAnimation, setDeletingAnimation] = useState(false);
    const [fileMovementAnimation, setFileMovementAnimation] = useState(false);
    const [fileMoveDialog, setFileMoveDialog] = useState(false);
    const [prevFolder, setPrevFolder] = useState<string | undefined>(undefined);
    const [offset, setOffset] = useState(0);
    const [fileMovementError, setFileMovementErrors] = useState<string[]>();
    const loading = mediaProps.isSearchLoading || (mediaProps.isListLoading && tableData?.length === 0);

    const itemsToShow = searchText.length > 0 ? searchedFiles : tableData;

    const storageSourceRef = useRef<StorageSourceCrud | undefined>(undefined);

    const handleIconClick = (icon: string) => {
        const {TABLE_VIEW_SELECTED, GRID_VIEW_SELECTED} = EventNames;
        const eventToTrack = icon === 'list' ? TABLE_VIEW_SELECTED : GRID_VIEW_SELECTED;

        Tracker.trackEvent(eventToTrack);
        setSelectedIcon(icon);
        const newTableViewValue = icon === 'list';
        setTableView(newTableViewValue);
        localStorage.setItem('tableView', newTableViewValue.toString());
    };

    const handleDragOverPseudo = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragDiv(true);
    };

    const handleDragNoAction = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragDiv(false);
    };

    let orgUid = () => userProfileProps.currentOrganization.uid;

    const dispatch = useAppDispatch();

    async function searchFiles() {
        // TODO : paginate this
        if (orgUid() && searchText.length > 1) {
            dispatch(
                searchMediaFiles({
                    orgUid: orgUid(),
                    q: searchText,
                    offset: 0,
                    limit: 50,
                    folder: folderName,
                }),
            );
        }
    }

    async function fetchFiles() {
        await dispatch(
            getMediaFiles({orgUid: orgUid(), folder: folderName, limit: PAGINATION_PAYLOAD, offset: offset}),
        );
    }

    const onFileDelete = async (fileUid: string | undefined) => {
        if (fileUid && fileUid !== '') {
            await fetchFiles();
        }
    };

    const forceRefreshFiles = () => {
        setTableData([]);
        fetchFiles();
    };

    const bulkDelete = async () => {
        if (selectedFiles) {
            setDeletingAnimation(true);
            const chunkSize = 15;
            for (let i = 0; i < selectedFiles.length; i += chunkSize) {
                const chunk = selectedFiles.slice(i, i + chunkSize);
                await Promise.all(
                    chunk.map(async (fileUid) => {
                        await dispatch(deleteMediaFile({orgUid: orgUid(), mediaFileUid: fileUid}));
                    }),
                );
            }
            setDeletingAnimation(false);
            setSelectAll(false);
            setSelectChecked(false);
            forceRefreshFiles();
        }
    };

    const onFileChildDelete = async (deleteFileUid: string, childUid: string) => {
        const tableTemp = tableData.slice();
        const parentIndex = tableTemp.findIndex((item) => item.file_uid === deleteFileUid);
        if (parentIndex !== -1) {
            const parentObject = tableTemp[parentIndex];
            const updatedSubRows = parentObject.subRows.filter((subRow: {uid: string}) => subRow.uid !== childUid);
            tableTemp[parentIndex] = {
                ...parentObject,
                subRows: updatedSubRows,
            };
            setTableData(tableTemp);
        } else {
            console.error('Parent object not found with file_uid:', deleteFileUid);
        }
    };

    const onUploading = async (fileData: any) => {
        await Promise.all(
            fileData.map(async (fileToUpload: {toUploadFile: any; file: any; options: any}) => {
                console.log(fileToUpload);
                await dispatch(
                    uploadFile({
                        params: {
                            file: fileToUpload?.file,
                            fileName: fileToUpload.options.fileName,
                            orgUid: orgUid(),
                            folder: folderName,
                            uploaded_type: '1',
                        },
                    }),
                );
            }),
        );
    };

    const handleButtonUpload = () => {
        tracker.trackEvent(EventNames.FILE_UPLOAD_CLICKED);
        const features = userProfileProps.currentOrganization.enabled_features;
        if (!features.includes(FeatureFlags.MEDIA_UPLOAD)) {
            tracker.trackEvent(EventNames.LOW_STORAGE_UPGRADE_NUDGE);
            setUpgradeNudge(true);
            return;
        }
        fileButtonUploadRef?.current?.click();
    };

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target?.files;
        Tracker.trackEvent(EventNames.FILE_UPLOAD_FROM_UPLOAD_BUTTON);
        setFiles(files);
    };

    const onFileDrop = (data: any) => {
        Tracker.trackEvent(EventNames.FILE_UPLOAD_BY_DRAGGING);
        setDraggedData(data);
    };

    const onFirstDrop = (data: any) => {
        setDraggedData(data);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const files = event.clipboardData?.files;

        if (files && files.length > 0) {
            const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
            const filteredFiles = Array.from(files).filter((file) => acceptedTypes.includes(file.type));

            if (filteredFiles.length > 0) {
                processFileToUpload(filteredFiles);
            } else {
                console.log('No files pasted or unsupported file types.');
            }
        }
    };

    const removeFromTable = (fileName: string) => {
        const modifiedTempData = uploadFileMeta.filter((item) => item.title !== fileName);
        const modifiedTableData = tableData.filter((item) => item.title !== fileName);
        setUploadFileMeta(modifiedTempData);
        setTableData(modifiedTableData);
    };

    const processFileToUpload = (data: FileList | null | undefined | File[]) => {
        const allFiles = uploadedFiles.map((item) => item.title.trim().toLowerCase());
        if (data && data.length > 0) {
            const filesArray = Array.from(data);
            const finalFilesArray: {
                file: File;
                options: {fileName: string; fileSize: number; fileType: string};
            }[] = [];

            for (const file of filesArray) {
                if (AllowedFileTypes.includes(file.type)) {
                    if (file.size > 16000000) {
                        toast.error(`File ${file.name} exceeds 16 MB. Please try with a smaller file.`);
                    } else {
                        let fileName = file.name;

                        if (allFiles.includes(fileName.trim().toLowerCase())) {
                            let name = fileName.split('.')[0];
                            let ext = fileName.split('.')[1];
                            fileName = `${name}${Date.now()}.${ext}`;
                        }
                        finalFilesArray.unshift({
                            file,
                            options: {
                                fileName: fileName,
                                fileSize: file.size,
                                fileType: file.type,
                            },
                        });
                    }
                } else {
                    toast.error(
                        `Unsupported file ${file.name}. Please upload only PNG, JPG, JPEG, SVG, GIF, or WEBP images.`,
                    );
                }
            }

            if (finalFilesArray.length > 0) {
                finalFilesArray.forEach((file) => {
                    const fileData = {
                        title: file.options.fileName,
                        cdn_url: '',
                        file_size: file.options.fileSize,
                        file_uid: '',
                        thumbnail_url: '',
                        progress: 45,
                        actionStatus: 'uploading',
                    };

                    setUploadFileMeta((prevFiles) => {
                        if (!prevFiles.find((prFile) => prFile?.title === fileData.title)) {
                            return [...prevFiles, fileData];
                        }
                        return [...prevFiles];
                    });
                });

                onUploading(finalFilesArray).then(async () => {
                    // refresh profile & hence metrics if there is a potential that user can exhaust storage credit post this upload.
                    if (metricsProps.usages.storage + 5242880 > metricsProps.limits.storage) {
                        await dispatch(refreshUserProfile());
                    }
                    await fetchFiles();
                });
            }
        }
    };

    const isAllSelected = (selected: boolean) => {
        setSelectChecked(selected);
        if (!selected) {
            setSelectedCount(0);
        }
    };

    const setSelectedChecked = () => {
        setSelectChecked(!selectAll);
    };

    const getSelectedData = (selectedUids: string[]) => {
        setSelectedFiles(selectedUids);
        setSelectedCount(selectedUids.length);
    };

    const resetClearState = () => {
        setClearSelection(false);
    };

    const fileMovementDialogClose = () => {
        setFileMoveDialog(false);
    };

    const confirmFileMovement = async (folderName: string) => {
        const chunkSize = 15;
        if (selectedFiles) {
            const errors: string[] = [];
            setFileMovementAnimation(true);
            for (let i = 0; i < selectedFiles.length; i += chunkSize) {
                const chunk = selectedFiles.slice(i, i + chunkSize);
                await Promise.all(
                    chunk.map(async (fileUid) => {
                        const res = await MoveFiles({fileUid: fileUid, folderName: folderName, orgUid: orgUid()});
                        if (res.status !== 200) {
                            if (res.error != null) {
                                errors.push(res.error);
                            }
                        }
                    }),
                );
            }
            if (errors.length > 0) {
                setFileMovementErrors(errors);
            }
            setFileMovementAnimation(false);
            setSelectAll(false);
            setSelectChecked(false);
            forceRefreshFiles();
        }
    };

    const handlePageClick = (event: any) => {
        const newOffset = (event.selected * PAGINATION_PAYLOAD) % mediaProps.totalResults;
        setOffset(newOffset);
    };

    useEffect(() => {
        if (uploadedFiles.length > 0) {
            setTableData((prevTableData) => [...uploadedFiles]);
        }
    }, [uploadedFiles]);

    useEffect(() => {
        if (storageSourceRef.current === undefined) {
            storageSourceRef.current = new StorageSourceCrudImpl(userProfileProps.currentOrganization.uid);
        } else {
            if (storageSourceRef.current.orgId !== userProfileProps.currentOrganization.uid) {
                storageSourceRef.current = new StorageSourceCrudImpl(userProfileProps.currentOrganization.uid);
            }
        }
    }, [userProfileProps.currentOrganization]);

    useEffect(() => {
        if (orgUid().length > 0) {
            if (folderName !== prevFolder) {
                setSearchText('');
                setPrevFolder(folderName);
                setOffset(0);
                forceRefreshFiles();
            } else {
                fetchFiles();
            }
        }
    }, [userProfileProps.currentOrganization, folderName, offset]);

    useEffect(() => {
        if (mediaProps.fileUploadList.length && uploadFileMeta.length) {
            const updatedUploadFileMeta = uploadFileMeta.map((uploadMeta) => {
                const matchingUpload = mediaProps.fileUploadList.find(
                    (fileUpload) => fileUpload.fileName === uploadMeta.title,
                );
                if (matchingUpload) {
                    if (matchingUpload.success) {
                        return {
                            ...uploadMeta,
                            success: true,
                            error: '',
                            actionStatus: 'uploaded',
                        };
                    } else if (matchingUpload.loading) {
                        return {
                            ...uploadMeta,
                            success: false,
                            error: '',
                            actionStatus: 'uploading',
                        };
                    } else if (matchingUpload.error !== '' && !matchingUpload.success) {
                        return {
                            ...uploadMeta,
                            success: false,
                            error: matchingUpload.error,
                            actionStatus: 'error',
                        };
                    }
                }

                return uploadMeta;
            });

            const updatedUploadFileMetaTest = updatedUploadFileMeta.filter((uploadMeta) => {
                return mediaProps.fileUploadList.find(
                    (fileUpload) => fileUpload.fileName === uploadMeta.title && !fileUpload.success,
                );
            });
            setTableData((prevTableData) => [...updatedUploadFileMetaTest, ...mediaProps.files]);
        }
    }, [mediaProps, uploadFileMeta]);

    useEffect(() => {
        if (files && files.length > 0) {
            processFileToUpload(files);
            fileButtonUploadRef.current.value = null;
        }
    }, [files]);

    useEffect(() => {
        if (orgUid() && searchText.length > 1) {
            Tracker.trackEvent(
                EventNames.MEDIA_SEARCH,
                {},
                {
                    q: searchText,
                    orgUid: orgUid(),
                    email: userProfileProps?.email,
                    userName: userProfileProps?.first_name,
                },
            );
            searchFiles();
        } else {
            fetchFiles();
        }
    }, [searchText]);

    useEffect(() => {
        processFileToUpload(draggedData);
    }, [draggedData]);

    // when all the items of a page is deleted & there are items in lower pages, offset to 0
    useEffect(() => {
        if (itemsToShow.length === 0 && offset > 0) {
            setOffset(0);
        }
    }, [itemsToShow]);

    useEffect(() => {
        setIsTabletOrLarger(window.innerWidth > 768);
        Tracker.setCurrentScreenName(ScreenNames.MEDIA_LIBRARY);
        Tracker.trackEvent(EventNames.SCREEN_VIEWS, {
            [EventProperty.ScreenName]: ScreenNames.MEDIA_LIBRARY,
        });

        if (routeFiles && routeFiles.length > 0) {
            processFileToUpload(routeFiles);
        }
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            navigate('/media-library', {state: null});
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const renderUploadFileBtn = () => {
        return (
            <div className='ml-[18px]'>
                <Button variant='primary' icon onClick={handleButtonUpload}>
                    <input
                        type='file'
                        accept='image/png, image/jpg, image/jpeg, image/webp, image/gif, image/svg, image/svg+xml'
                        multiple
                        onChange={handleFiles}
                        ref={fileButtonUploadRef}
                        hidden
                    />
                    <Plus />
                    File Upload
                </Button>
            </div>
        );
    };

    const renderLoader = useMemo(
        () => (
            <div className='m-20 flex justify-center'>
                <CommonSpinLoader size={'sm'} />
            </div>
        ),
        [],
    );

    return (
        <Dialog open={upgradeNudge}>
            <div
                className={'flex-1'}
                onDragOver={handleDragOverPseudo}
                onDragLeave={handleDragNoAction}
                onDrop={handleDragNoAction}
                onPaste={handlePaste}
                tabIndex={0}>
                <FileMovement
                    open={fileMoveDialog}
                    onClose={fileMovementDialogClose}
                    confirmMove={confirmFileMovement}
                    folderName={folderName}
                    orgId={orgUid()}
                />
                <Dialog open={fileMovementAnimation}>
                    <DialogContent className='flex w-[300px] items-center justify-center'>
                        <FileMovementAnimation />
                    </DialogContent>
                </Dialog>
                <Dialog open={fileMovementError && fileMovementError?.length > 0}>
                    <DialogContent
                        className='flex w-[700px] flex-col items-center justify-center'
                        onClose={() => setFileMovementErrors([])}>
                        <DialogTitle>File Movement Errors</DialogTitle>
                        <div className='my-5 flex flex-col'>
                            <ul className='list-disc'>
                                {fileMovementError?.map((error, index) => <li key={index}>{error}</li>)}
                            </ul>
                        </div>
                        <Button onClick={() => setFileMovementErrors([])}>Acknowledge</Button>
                    </DialogContent>
                </Dialog>
                {/*web header*/}
                <div className='sticky z-50 hidden overflow-y-auto bg-white md:block'>
                    <Breadcrumbs items={breadcrumbItems}>
                        <div className={'mx-2 w-[300px]'}>
                            <SearchBar onTextChange={setSearchText} />
                        </div>
                        {tableData.length !== 0 ? (
                            <div className='flex gap-[18px]'>
                                <div className='flex items-center justify-center gap-[0.25px] rounded-[9px] bg-[#EAECF0] px-[0.25rem] py-[0.25rem]'>
                                    <motion.div
                                        className={`flex h-full cursor-pointer items-center rounded-[0.5rem] p-[6px] 
            ${selectedIcon === 'list' ? 'bg-[#FFFFFF]' : 'bg-[#EAECF0]'}`}
                                        onClick={() => handleIconClick('list')}
                                        layout
                                        transition={spring}
                                        whileHover={{scale: 1.1}}>
                                        <ListIcon width={20} height={20} />
                                    </motion.div>
                                    <motion.div
                                        className={`flex h-full cursor-pointer items-center rounded-[0.5rem] p-[6px] 
            ${selectedIcon === 'grid' ? 'bg-[#FFFFFF]' : 'bg-[#EAECF0]'}`}
                                        onClick={() => handleIconClick('grid')}
                                        layout
                                        transition={spring}
                                        whileHover={{scale: 1.1}}>
                                        <GridIcon width={20} height={20} />
                                    </motion.div>
                                </div>
                            </div>
                        ) : null}
                        {renderUploadFileBtn()}
                    </Breadcrumbs>
                </div>

                {/*mobile header*/}
                <div className='sticky top-0 z-50 flex items-center justify-between border-b border-[#EAECF0] bg-white py-2 md:hidden'>
                    <div className='ml-3 flex'>
                        <span className='text-[12px] font-[600]'>Media Library</span>
                    </div>
                    <div className={'w-[220px]'}>
                        <SearchBar onTextChange={setSearchText} />
                    </div>
                    <div className='mx-1 flex'>
                        {tableData.length !== 0 ? (
                            <div className='flex gap-[18px]'>
                                <Button
                                    className='py-[9.5px] text-[14px] font-[600] text-[#155EEF]'
                                    variant='nude'
                                    size='sm'
                                    onClick={handleButtonUpload}>
                                    <input
                                        type='file'
                                        multiple
                                        onChange={handleFiles}
                                        ref={fileButtonUploadRef}
                                        hidden
                                    />
                                    + New
                                </Button>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className='mx-10 my-[21px] flex items-center justify-center gap-4 rounded-[11px] bg-[#EAECF5] px-4 py-5 text-[12px] font-[500] text-[#4E5BA6]'>
                    <Lightbulb />
                    <p>
                        Now add your images through methods such as drag and drop, uploading files, uploading folders,
                        and utilizing keyboard shortcuts like CTRL + C and CTRL + V.
                    </p>
                </div>

                {/*Loader.....*/}
                {loading && renderLoader}

                {searchText.length > 0 && !loading && (
                    <div className='px-2 pt-2 md:px-10 md:pt-4'>
                        {searchedFiles.length > 0 ? (
                            <>
                                <span className='text-inter text-[14px] text-gray-500'>Search results for </span>
                                <span className='font-[600]'>{searchText}</span>
                                <span className='text-inter text-[14px] text-gray-500'> in the media library</span>
                            </>
                        ) : (
                            <>
                                <span className='text-inter text-[14px] text-gray-500'>
                                    Sorry, we couldn't find any files matching your search for{' '}
                                </span>
                                <span className='font-[600]'>{searchText}</span>
                            </>
                        )}
                    </div>
                )}

                {!folderName ? (
                    !loading && searchText.length === 0 ? (
                        <>
                            <div className={'my-[30px] rounded-[12px] border border-[#EAECF0] md:mx-[40px]'}>
                                <div className='px-[1.5rem] py-[1.5rem] pb-[1.5rem]'>
                                    <div className={'flex flex-row items-center'}>
                                        <span className='text-[18px] font-[700] text-[#000000]'>Folders</span>
                                        <div className={'flex-end ml-auto'}>
                                            <NewFolder
                                                storageSourceCrud={storageSourceRef.current}
                                                onNewFolder={() => {
                                                    forceRefreshFiles();
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {tableView && isTabletOrLarger ? (
                                    <>
                                        <FolderList
                                            storageSourceCrud={storageSourceRef.current}
                                            folders={mediaProps.folders}
                                            onFolderClick={(folder) => {
                                                navigate(`/media-library/${folder.name}`);
                                            }}
                                            onFolderDelete={() => {
                                                forceRefreshFiles();
                                            }}
                                            forceRefresh={forceRefreshFiles}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <FolderGrid
                                            storageSourceCrud={storageSourceRef.current}
                                            folders={mediaProps.folders}
                                            onFolderClick={(folder) => {
                                                navigate(`/media-library/${folder.name}`);
                                            }}
                                            onFolderDelete={() => {
                                                forceRefreshFiles();
                                            }}
                                            forceRefresh={forceRefreshFiles}
                                        />
                                    </>
                                )}
                            </div>
                        </>
                    ) : null
                ) : null}

                {/*No Files to show....*/}
                {!loading && itemsToShow?.length === 0 && (
                    <MediaNoContent
                        showGettingStarted={!folderName && mediaProps.folders.length === 0}
                        onFileDrop={onFirstDrop}
                    />
                )}

                {/**/}
                {!loading && itemsToShow && itemsToShow.length > 0 && (
                    <>
                        <div className='my-[30px] rounded-[12px] border border-[#EAECF0] md:mx-[40px]'>
                            <div className='flex items-center justify-between px-[1.5rem] py-[1.5rem] pb-[1.5rem]'>
                                <span className='text-[18px] font-[700] text-[#000000]'>Files</span>
                                <div className='flex items-center justify-center gap-3 font-[400] text-[#667085]'>
                                    <span>Select All</span>
                                    <input
                                        type='checkbox'
                                        checked={selectChecked}
                                        className='bg[#D0D5DD]'
                                        onChange={() => {
                                            setSelectAll(!selectAll);
                                            setSelectedChecked();
                                        }}
                                    />
                                    {selectedCount > 0 && (
                                        <div className='flex items-center justify-center gap-5 font-[400] text-[#667085]'>
                                            <span className='text-[#17B26A]'>{selectedCount} items selected</span>
                                            <div
                                                className='flex cursor-pointer items-center justify-center gap-2'
                                                onClick={() => setShowDeleteModal(true)}>
                                                <TrashIcon />
                                                Delete
                                            </div>
                                            <div
                                                className='hidden cursor-pointer items-center justify-center gap-2 md:flex'
                                                onClick={() => setFileMoveDialog(true)}>
                                                <MoveIcon />
                                                Move
                                            </div>
                                            <div
                                                className='hidden cursor-pointer items-center justify-center gap-2 md:flex'
                                                onClick={() => {
                                                    if (selectedFiles) {
                                                        navigate(
                                                            `/bulkTransformation/${encodeURIComponent(selectedFiles.join(','))}`,
                                                        );
                                                    }
                                                }}>
                                                <BulkEditIcon />
                                                Bulk Edit
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger>
                                                    <Ellipses />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className='mx-7'>
                                                    <DropdownMenuItem>
                                                        <ImageCsv fileUids={selectedFiles} orgUid={orgUid()} />
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            <DeleteFile
                                                fileName={`the selected ${selectedCount} ${selectedCount > 1 ? 'files' : 'file'}`}
                                                onClose={() => setShowDeleteModal(false)}
                                                onConfirm={() => {
                                                    bulkDelete().then((r) => {
                                                        setClearSelection(true);
                                                    });
                                                    setShowDeleteModal(false);
                                                }}
                                                open={showDeleteModal}
                                            />
                                            <Dialog open={deletingAnimation}>
                                                <DialogContent className='flex w-[300px] items-center justify-center'>
                                                    <DeleteAnimation />
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {tableView && isTabletOrLarger ? (
                                <>
                                    <TableListView
                                        files={itemsToShow ?? []}
                                        onFileDrop={onFileDrop}
                                        orgId={orgUid()}
                                        onFileDelete={onFileDelete}
                                        onFileChildDelete={onFileChildDelete}
                                        removeFromTable={removeFromTable}
                                        dragDiv={dragDiv}
                                        selectAll={selectAll}
                                        isAllSelected={isAllSelected}
                                        getSelectedData={getSelectedData}
                                        clearSelection={clearSelection}
                                        resetClearState={resetClearState}
                                    />
                                    <Pagination
                                        pageCount={Math.ceil(mediaProps.totalResults / PAGINATION_PAYLOAD)}
                                        onPageChange={handlePageClick}
                                    />
                                </>
                            ) : (
                                <>
                                    <MediaWithContent
                                        files={itemsToShow ?? []}
                                        orgId={orgUid()}
                                        onFileDelete={onFileDelete}
                                        onFileChildDelete={onFileChildDelete}
                                        removeFromTable={removeFromTable}
                                        selectAll={selectAll}
                                        isAllSelected={isAllSelected}
                                        getSelectedData={getSelectedData}
                                        clearSelection={clearSelection}
                                        resetClearState={resetClearState}
                                    />
                                    <Pagination
                                        pageCount={Math.ceil(mediaProps.totalResults / PAGINATION_PAYLOAD)}
                                        onPageChange={handlePageClick}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}
                <ToastContainer position={'bottom-right'} autoClose={5000} />
            </div>
            <DialogContent
                className='w-[320px] rounded-2xl bg-[#363F72] md:w-[400px]'
                onClose={() => {
                    setUpgradeNudge(false);
                }}>
                <div className={'pt-8 text-[16px]'}>
                    <UpgradeCard
                        msg={'Need more space for your awesome files? Upgrade your plan now & keep uploading!'}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};
export default MediaLibrary;
